import { gql, useLazyQuery } from '@apollo/client';
import { cloneDeep } from 'lodash-es';
import { useEffect } from 'react';
import { PusherEvent } from '@marvelapp/core';
import { useConfig, useCurrentUser, usePusher } from './react';
const FETCH_UPDATED_SESSION = gql `
  query fetchUpdatedSession($roomName: String!) {
    liveSession(roomName: $roomName) {
      observers {
        avatarUrl
        displayName
      }
      researchers {
        avatarUrl
        displayName
      }
      testRecording {
        egressStartTime
        pk
        roomName
        roomStartTime
      }
    }
  }
`;
export function useRefetchLiveSessions(refetchSessions, getCacheId, projectPk) {
    const config = useConfig();
    const user = useCurrentUser();
    const { pusher } = usePusher();
    const [fetchSession, { client, error }] = useLazyQuery(FETCH_UPDATED_SESSION);
    useEffect(() => {
        const channelName = `${config.pusher.userChannelPrefix}${user.pk}`;
        const { bindChannel } = pusher.subscribe(channelName);
        const handleSessionStarted = (eventData) => {
            if (eventData.content.usertest_pk === projectPk || !projectPk) {
                refetchSessions();
            }
        };
        const handleSessionUpdated = (eventData) => {
            if (eventData.content.usertest_pk === projectPk || !projectPk) {
                fetchSession({
                    variables: {
                        roomName: eventData.content.room_name,
                    },
                });
            }
        };
        const handleSessionEnded = (eventData) => {
            if (eventData.content.usertest_pk === projectPk || !projectPk) {
                refetchSessions();
            }
        };
        const unbindSessionStarted = bindChannel(PusherEvent.LiveSessionStarted, handleSessionStarted);
        const unbindSessionUpdated = bindChannel(PusherEvent.LiveSessionUpdated, handleSessionUpdated);
        const unbindSessionEnded = bindChannel(PusherEvent.LiveSessionEnded, handleSessionEnded);
        return () => {
            unbindSessionStarted();
            unbindSessionUpdated();
            unbindSessionEnded();
        };
    }, [
        config.pusher.userChannelPrefix,
        pusher,
        user.pk,
        fetchSession,
        projectPk,
        refetchSessions,
    ]);
    useEffect(() => {
        if (error === null || error === void 0 ? void 0 : error.message) {
            const [_, roomName] = error.message.split(':');
            if (!roomName)
                return;
            const cacheId = getCacheId(client.cache);
            if (!cacheId)
                return;
            client.cache.modify({
                id: cacheId,
                fields: {
                    liveSessions(existingLiveSessionRefs) {
                        const newLiveSessionRefs = cloneDeep(existingLiveSessionRefs);
                        newLiveSessionRefs.edges = newLiveSessionRefs.edges.filter((edge) => {
                            const currentRoomName = client.cache.identify(edge.node);
                            return !(currentRoomName === null || currentRoomName === void 0 ? void 0 : currentRoomName.includes(roomName.trim()));
                        });
                        return newLiveSessionRefs;
                    },
                },
            });
        }
    }, [client.cache, error, getCacheId, user.company.pk]);
}
